import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { animated, useSpring } from '@react-spring/web';
import config from 'config';
import PropTypes from 'prop-types';
import { FaExclamationCircle } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { Checkbox } from '@techstyle/react-components-legacy';
import { FormattedMessage } from '@techstyle/react-intl';
import { useDomain } from '@techstyle/redux-core';

import { setSizesFromBraCalculator } from '../../slices/braSizeSelections';
import { mobile } from '../../styles';
import {
  categorizeBraSize,
  formatShoeSizes,
  getProductCategoryTypes,
} from '../../utils/productDetail';
import {
  getProductReviews,
  getRegion,
  isCurvySize,
  isPreorderable as checkIsPreorderable,
} from '../../utils/selectors';
import {
  getAllGenderEquivalentSizes,
  getBandSizeList,
  getCupSizeList,
  getHarnessSizes,
  getInfoSizeList,
  getNonBraSizes,
  getShoeSizes,
  getSlipperSizes,
  hasShoeSize,
  splitCombinedSize,
} from '../../utils/sizes';
import { Component as LowStockFlag } from '../LowStockFlag';
import { Component as ProductDetailSizeLinks } from '../ProductDetailSizeLinks';

const defaultCategories = config.get('public.productBrowser.sections');

const accessoriesCategoryId =
  defaultCategories.accessories.defaultProductCategoryIds[0];

const allGenderCategoryId =
  defaultCategories.allGender.defaultProductCategoryIds[0];

const locationLowStockIndicatorMasterProduct = config.get(
  'public.quantityRules.locationLowStockIndicator.masterProduct'
);

const SIZE_REQUIRED = 'SIZE_REQUIRED';
const SIZE_UNAVAILABLE = 'SIZE_UNAVAILABLE';
const SELECT_CUP_SIZE = 'SELECT_CUP_SIZE';

const Container = styled.div`
  margin-bottom: ${props =>
    props.shouldShowBottomSizeAndFitGuide
      ? props.theme.spacing.moderate
      : '0'}px;
`;

const SizeCategoryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-bottom: ${props =>
    props.isProductSet ? 0 : props.theme.spacing.small}px;

  ${mobile`
    margin-bottom: ${props =>
      props.isProductSet ? 0 : props.theme.spacing.moderate}px;

    ${props =>
      props.isProductUnisex && `margin-bottom: ${props.theme.spacing.small}px;`}
  `};

  ${props =>
    (props.shouldShowBottomSizeAndFitGuide || props.isProductUnisex) &&
    `
      margin-bottom: ${props.theme.spacing.small}px;
  `}
`;

const CupSizeWrapper = styled.ul``;

const BandSizeWrapper = styled.div``;

const BandListLabel = styled.span``;

const CupLabel = styled.li`
  line-height: 14px;
  text-align: center;
`;

const SizeSectionWrap = styled.div`
  display: flex;
  flex-wrap: wrap;

  margin-bottom: ${props => props.theme.spacing.small}px;
`;

const SizeCategoryLabel = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.small}px;
  margin-top: ${props => props.theme.spacing.moderate}px;

  ${({ theme }) => theme.paragraph.variants.paragraph4Uppercase.textStyles}
  color: ${({ theme }) => theme.colors.grey};
`;

// This allows us to change styling on bras & non-bras in styled-components within
// other components (i.e. to hide/show in specific scenarios)
const BraSizeCategoryLabel = styled(SizeCategoryLabel)`
  margin-top: ${props => props.theme.spacing.small}px;
`;

const ProductFitReview = styled.div`
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  ${mobile`margin-left:auto;`}
`;
const FitLabel = styled.span`
  font-size: 12px;
  margin-left: 5px;
  align-items: bottom;
  margin-right: ${props => props.theme.spacing.tiny}px;
`;

const AllGenderFitLabel = styled.p`
  font-size: 14px;
  line-height: 16.8px;
  letter-spacing: 0.3px;
`;

const StyledBoldAllGenderFitLabel = styled.span`
  font-weight: 700;
  margin-right: 4px;
`;

const FitLink = styled.a`
  margin-left: 4px;

  ${({ theme }) => theme.paragraph.variants.paragraph4Uppercase.textStyles}
  color: ${({ theme }) => theme.colors.grey};
`;

const StyledProductDetailSizeLinks = styled(ProductDetailSizeLinks)`
  font-weight: normal;
  letter-spacing: 0.58px;
  color: ${props => props.theme.colors.active};
`;

const SizeCheckbox = styled(Checkbox)`
  margin: 4px 4px 4px;
  display: inline-block;
  ${mobile`
    margin: 4px 12px 8px 0px;
  `};

  > ${Checkbox.InputWrapper} {
    border: none;
    position: relative;
    justify-content: center;

    > ${Checkbox.CustomCheckbox} {
      z-index: -1;
      width: 50px;
      height: 50px;
      border-radius: 50%;
      border: ${props => (props.isAvailable ? '1px solid' : '0px')};

      border-color: ${props => props.theme.colors.stone};

      background-color: ${props =>
        props.isAvailable
          ? props.theme.colors.white
          : props.theme.colors.gray150};
      margin: 0;

      > svg {
        display: none;
      }
    }

    > ${Checkbox.Label} {
      position: absolute;
      font-size: 14px;
      font-weight: normal;
      color: ${props =>
        props.isAvailable
          ? props.theme.colors.default
          : props.theme.colors.subdued};
      top: 14px;
      ${({ isSizeDisplayableInNewLine }) =>
        isSizeDisplayableInNewLine
          ? css`
              top: 5px;
              font-size: 12px;
              text-align: center;
              white-space: pre-line;
            `
          : ''}
    }

    input:checked ~ ${Checkbox.Label} {
      color: ${props => props.theme.colors.white};
    }

    input:active
      + ${Checkbox.CustomCheckbox},
      input:focus
      + ${Checkbox.CustomCheckbox} {
      border: 1px solid ${props => props.theme.colors.gunmetal};
    }

    input:checked + ${Checkbox.CustomCheckbox} {
      background-color: ${props => props.theme.colors.default};
    }

    input:checked:focus + ${Checkbox.CustomCheckbox} {
      border: 1px solid ${props => props.theme.colors.black};

      border-radius: 100%;
      background-clip: content-box;
      padding: 1px;
    }
  }
`;

const BraCheckbox = styled(SizeCheckbox)`
  > ${Checkbox.InputWrapper} {
    > ${Checkbox.CustomCheckbox} {
      > svg {
        display: none;
      }
    }

    > ${Checkbox.Label} {
      top: 14px;
    }

    input:checked + ${Checkbox.CustomCheckbox} {
      border: 0;
      padding: 0;
    }
  }
`;

const CupSizeCheckbox = styled(BraCheckbox)`
  > ${Checkbox.InputWrapper} {
    > ${Checkbox.Label} {
      top: 17px;
    }
  }
`;

const OneSizeCheckbox = styled(SizeCheckbox)`
  > ${Checkbox.InputWrapper} {
    > ${Checkbox.Label} {
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 10px;
      font-weight: normal;
      text-transform: uppercase;
      text-align: center;
      padding: 4px;

      ${mobile`
        font-size: 12px;
        font-weight: 500;
      `};
    }
  }
`;

const SizeSelectError = styled.span`
  font-size: 12px;
  font-weight: 300;
  color: ${props => props.theme.colors.error};
  display: block;
  height: 16px;
  text-transform: none;
  letter-spacing: normal;
`;

const StyledFaExclamationCircle = styled(FaExclamationCircle)`
  margin-right: 4px;
  padding-top: 3px;
`;

const LowStockFlagWrapper = styled(animated.div)`
  display: flex;
  align-items: center;
`;

const TextWrapper = styled(animated.div)`
  width: 100%;
`;

const StyledLowStockFlag = styled(LowStockFlag)`
  background-color: transparent;
  color: ${({ theme }) => theme.colors.green500};
  font-size: 14px;
  padding: 0px;
`;

function ProductDetailSizeSelector({
  displayBraSize,
  error,
  label = 'Size',
  getSizeLabel,
  isCombinedSet,
  isGwpSelection,
  isProductSet = false,
  isProductUnisex = false,
  onChooseSize,
  product = {
    related_product_id_object_list: [],
    product_category_id_list: [],
  },
  selectedColorLabel,
  selectedSize,
  setIndex,
  sizes,
  shouldForceAvailable = false,
  shouldHideFitAndSizeGuide = false,
  shouldHideProductFitReviewPipe,
  shouldShowBottomSizeAndFitGuide = false,
  shouldSplitCombinedSize,
}) {
  const { tld } = useDomain();
  const isCurvy = useSelector(state => isCurvySize(state, { product }));
  const isPreorderable = useSelector(state =>
    checkIsPreorderable(state, { product })
  );

  const reviews = useSelector(state =>
    getProductReviews(state, { product, isSet: isProductSet })
  );
  const infoSizeList = getInfoSizeList(
    tld,
    isCurvy,
    isProductSet,
    isCombinedSet
  );
  const bandSizesList = getBandSizeList(infoSizeList);
  const cupSizesList = getCupSizeList(infoSizeList);
  const shoeSizeList = getShoeSizes(tld);
  const slipperSizeList = getSlipperSizes(tld);
  const harnessSizeList = getHarnessSizes(tld);
  const nonBraSizesList = getNonBraSizes({
    tld,
    product,
    isCurvy,
    isSet: isProductSet,
    displayFullRangeSize: isCombinedSet,
  });

  const {
    product_category_id_list: productCategoryIdList,
    tag_id_list: tagIdList,
  } = product;
  const { isSlippers, isHarness } = getProductCategoryTypes(
    productCategoryIdList,
    tagIdList
  );

  const warehouseLocation = useSelector(getRegion);

  // Low Size Selected flag animation
  const [isVisible, setIsVisible] = useState(false);
  const expand = useSpring({
    config: { mass: 1, tension: 170, friction: 26 },
    height: isVisible ? '30px' : '0px',
  });
  const fadeText = useSpring({
    config: { duration: 200 },
    opacity: isVisible ? 1 : 0,
  });

  const categorySizes = useMemo(
    () => (sizes ? categorizeBraSize(sizes) : []),
    [sizes]
  );

  const lowStockRules =
    locationLowStockIndicatorMasterProduct[warehouseLocation];
  const selectedSizeAvailableQuantity =
    selectedSize?.sizeProduct?.available_quantity;
  const selectedSizeValue = selectedSize?.sizeProduct
    ? getSizeLabel(selectedSize.sizeProduct)
    : selectedSize?.value;
  const lowSizeStockScale =
    selectedSizeAvailableQuantity != null &&
    selectedSizeAvailableQuantity > 0 &&
    selectedSizeAvailableQuantity <= lowStockRules.lowSizeStockMax;

  useEffect(() => {
    if (lowSizeStockScale && selectedSizeValue && !isGwpSelection) {
      setIsVisible(true);
    }
    return () => {
      setIsVisible(false);
    };
  }, [isGwpSelection, lowSizeStockScale, selectedSizeValue]);

  const renderLowSizeStockFlag = () => {
    return (
      <LowStockFlagWrapper style={expand}>
        <TextWrapper style={fadeText}>
          <StyledLowStockFlag
            isMasterProduct
            sizeQuantity={selectedSizeAvailableQuantity}
            selectedSize={selectedSizeValue}
          />
        </TextWrapper>
      </LowStockFlagWrapper>
    );
  };

  const renderError = (error, field) => {
    let errorMessage = null;
    switch (error) {
      case SIZE_REQUIRED:
        if (field === 'band') {
          errorMessage = (
            <FormattedMessage
              id="site_pdp.select_band_size_error_message"
              defaultMessage="Band Selection Required"
            />
          );
        } else if (field === 'cup') {
          errorMessage = (
            <FormattedMessage
              id="site_pdp.select_cup_size_error_message"
              defaultMessage="Cup Selection Required"
            />
          );
        } else {
          errorMessage = (
            <FormattedMessage
              id="site_pdp.select_size_error_message"
              defaultMessage="Size Selection Required"
            />
          );
        }
        break;
      case SIZE_UNAVAILABLE:
        if (field === 'band') {
          return null;
        } else {
          errorMessage = (
            <FormattedMessage
              id="site_pdp.select_another_size_nonset_error_message"
              defaultMessage="{size} in {color} is unavailable"
              values={{
                size: selectedSize?.sizeProduct
                  ? getSizeLabel(selectedSize.sizeProduct)
                  : selectedSize.value,
                color: selectedColorLabel,
              }}
            />
          );
        }
        break;
      case SELECT_CUP_SIZE:
        if (field === 'cup') {
          errorMessage = (
            <FormattedMessage
              id="site_pdp.select_cup_size_error_message"
              defaultMessage="Cup Selection Required"
            />
          );
        }
        break;
      default:
        errorMessage = error || null;
    }
    return (
      <SizeSelectError data-autotag="pdp-select-size-error">
        <StyledFaExclamationCircle size={13} />
        {errorMessage}
      </SizeSelectError>
    );
  };

  const onSelectCupSize = (size, cupSize, field) => {
    if (field === 'cupSize') {
      onChooseSize(cupSize, field);
    } else {
      onChooseSize(size, field);
    }
  };

  // This function gets sizes that are available in any color
  const getSizesAvailableForColors = product => {
    const { related_product_id_object_list: relatedProductIdObjectList } =
      product;

    const sizes = relatedProductIdObjectList.reduce(
      (sizesAvailableForColors, relatedProductIdObject) => {
        const { product_id_object_list: productIdObjectList } =
          relatedProductIdObject;

        if (Array.isArray(productIdObjectList) && productIdObjectList.length) {
          productIdObjectList.forEach(productIdObject => {
            const quantityKey = isPreorderable
              ? 'available_quantity_preorder'
              : 'available_quantity';
            const { [quantityKey]: availableQuantity } = productIdObject;
            if (availableQuantity <= 0) {
              return;
            }
            sizesAvailableForColors = {
              ...sizesAvailableForColors,
              [productIdObject.size]: { value: productIdObject.size },
            };
          });
        }
        return sizesAvailableForColors;
      },
      {}
    );
    const sizesArray = Object.values(sizes);

    return hasShoeSize(product)
      ? formatShoeSizes(sizesArray)
      : categorizeBraSize(sizesArray);
  };
  const dispatch = useDispatch();
  const handleShopMySize = useCallback(
    ({ bra, bralette }) => {
      const currentSelectionId = displayBraSize ? bra : bralette;
      const currentSelection = categorySizes.find(
        ({ id }) => id === currentSelectionId
      );
      if (currentSelection) {
        dispatch(
          setSizesFromBraCalculator({
            sizes: { bra, bralette },
            isBra: displayBraSize,
            currentSelection,
          })
        );
      }
    },
    [categorySizes, dispatch, displayBraSize]
  );

  // Note: Even if a product is "One Size" possible for a product to have a linked product that
  // is also "One Size". Because of this we will iterate over the size list instead of just rendering
  // one item.
  // Since the `id`/`value` of the size can be `OSFM` for both, we need to make sure to not have
  // duplicates. To do this we'll need to fake the keys/values using the derived sizes
  const renderOneSize = () => {
    const hasMultipleOneSize = sizes.length > 1;

    const sizeList = sizes
      ? sizes.map((displayedSize, index) => {
          if (displayedSize) {
            const isAvailable = !displayedSize.isSoldOut;
            const className = isAvailable ? 'available' : 'unavailable';
            const derivedValue = hasMultipleOneSize
              ? displayedSize.labelString
              : displayedSize.id;
            return (
              <div data-autotag="pdpSizeOneSize" key={derivedValue}>
                <OneSizeCheckbox
                  data-testid={displayedSize.label}
                  className={className}
                  checked={
                    selectedSize &&
                    displayedSize.value === selectedSize.value &&
                    selectedSize.sizeProduct.product_id ===
                      displayedSize.productId
                  }
                  value={derivedValue}
                  label={displayedSize.label}
                  name={derivedValue}
                  onChange={() => {
                    onChooseSize(displayedSize, 'size');
                  }}
                  isAvailable={isAvailable}
                />
              </div>
            );
          }
        })
      : [];

    const sizeReview = getSizeReview();

    const isAccessories = product.product_category_id_list.includes(
      accessoriesCategoryId
    );

    return (
      <SizeCategoryWrapper
        shouldShowBottomSizeAndFitGuide={shouldShowBottomSizeAndFitGuide}
        isProductSet={isProductSet}
        isProductUnisex={isProductUnisex}
      >
        <SizeCategoryLabel>
          {error ? (
            renderError(error)
          ) : (
            <FormattedMessage defaultMessage="Size" id="site_pdp.size_label" />
          )}
          {!error && !shouldHideFitAndSizeGuide && sizeReview ? (
            <ProductFitReview>
              <FitLabel defaultMessage="Fit" id="site_pdp.fit_label">
                <FormattedMessage
                  defaultMessage="Fit"
                  id="site_pdp.fit_label"
                />
                <span>:</span>
                <FitLink href="#average-headers">{sizeReview}</FitLink>
                {!isProductSet &&
                  !isAccessories &&
                  !shouldShowBottomSizeAndFitGuide &&
                  !shouldHideProductFitReviewPipe &&
                  '|'}
              </FitLabel>
              {!isProductSet && !shouldShowBottomSizeAndFitGuide && (
                <StyledProductDetailSizeLinks
                  product={product}
                  handleShopMySize={handleShopMySize}
                />
              )}
            </ProductFitReview>
          ) : (
            !isProductSet &&
            !shouldHideFitAndSizeGuide &&
            !shouldShowBottomSizeAndFitGuide && (
              <StyledProductDetailSizeLinks
                product={product}
                handleShopMySize={handleShopMySize}
              />
            )
          )}
        </SizeCategoryLabel>
        {renderLowSizeStockFlag()}
        <SizeSectionWrap>{sizeList}</SizeSectionWrap>
      </SizeCategoryWrapper>
    );
  };

  const renderSizes = () => {
    const sizesAvailableForColors = getSizesAvailableForColors(product);
    const sizeList = nonBraSizesList
      ? nonBraSizesList.map(displayedSize => {
          const displayedSizeValue = displayedSize.toString();
          const sizeInfo = sizes.find(
            size => size.value.toString() === displayedSizeValue
          );
          if (!sizeInfo) {
            return null;
          }
          const isSizeAvailableForColor = sizesAvailableForColors.some(
            sizeAvailable => {
              return sizeAvailable.value.toString() === displayedSizeValue;
            }
          );

          const isAvailable =
            shouldForceAvailable ||
            (sizeInfo && !sizeInfo.isSoldOut && isSizeAvailableForColor);
          const className = isAvailable ? 'available' : 'unavailable';

          const { splitSize, sizeSplitter } = splitCombinedSize(displayedSize);
          const isSizeDisplayableInNewLine =
            shouldSplitCombinedSize &&
            splitSize.length > 1 &&
            splitSize.some(size => size.length > 3);

          const labelDisplayedSize =
            isSizeDisplayableInNewLine && sizeSplitter === ' ' ? (
              <span>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span
                    style={{
                      fontSize: 12,
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {splitSize[0]}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {splitSize[1]}
                  </span>
                </div>
              </span>
            ) : displayedSizeValue.length > 4 ? (
              <span style={{ fontSize: 11 }}>{displayedSizeValue}</span>
            ) : (
              displayedSizeValue
            );

          const selectedSizeValue = selectedSize?.value?.toString();
          const checked = displayedSizeValue === selectedSizeValue;
          let autoTag = isAvailable ? 'pdpRegularSizes' : null;

          if (checked) {
            autoTag = 'pdpRegularSelectSize';
          }

          if (isProductSet) {
            const tagIndex = setIndex + 1;
            autoTag = isAvailable ? `pdpSet${tagIndex}RegularSizes` : null;
            if (checked) {
              autoTag = `pdpSet${tagIndex}RegularSelectSize`;
            }
          }

          return (
            <div key={displayedSizeValue} data-autotag={autoTag}>
              <SizeCheckbox
                data-testid={`size-${displayedSizeValue}`}
                className={className}
                checked={checked}
                value={displayedSizeValue}
                label={labelDisplayedSize}
                name={displayedSizeValue}
                onChange={() => {
                  onChooseSize(sizeInfo || displayedSizeValue, 'size');
                }}
                isAvailable={isAvailable}
                isSizeDisplayableInNewLine={isSizeDisplayableInNewLine}
              />
            </div>
          );
        })
      : [];

    const sizeReview = getSizeReview();

    const isAccessories = product.product_category_id_list.includes(
      accessoriesCategoryId
    );

    const isAllGenderProduct =
      product.product_category_id_list.includes(allGenderCategoryId);

    const isUnavailableSize = error === 'SIZE_UNAVAILABLE';

    const renderAllGenderFitEquivalent = () => {
      const allGenderEquivalentSizes = getAllGenderEquivalentSizes(
        tld,
        selectedSize.value
      );
      return (
        <>
          {allGenderEquivalentSizes?.womens ? (
            <FormattedMessage
              id="site_pdp.all_gender_fits_like_womens"
              defaultMessage="Women's {womensSize}"
              values={{
                womensSize: allGenderEquivalentSizes.womens,
              }}
            />
          ) : null}
          {allGenderEquivalentSizes?.womens &&
          allGenderEquivalentSizes?.mens ? (
            <span>, </span>
          ) : null}
          {allGenderEquivalentSizes?.mens ? (
            <FormattedMessage
              id="site_pdp.all_gender_fits_like_mens"
              defaultMessage="Men's {mensSize}"
              values={{
                mensSize: allGenderEquivalentSizes.mens,
              }}
            />
          ) : null}
        </>
      );
    };

    return (
      <SizeCategoryWrapper
        shouldShowBottomSizeAndFitGuide={shouldShowBottomSizeAndFitGuide}
        isProductSet={isProductSet}
        isProductUnisex={isProductUnisex}
      >
        <SizeCategoryLabel>
          {error ? (
            renderError(error)
          ) : (
            <FormattedMessage defaultMessage="Size" id="site_pdp.size_label" />
          )}
          {!error && !shouldHideFitAndSizeGuide && sizeReview ? (
            <ProductFitReview>
              {!isAllGenderProduct ? (
                <FitLabel>
                  <FormattedMessage
                    defaultMessage="Fit"
                    id="site_pdp.fit_label"
                  />
                  <span>:</span>
                  <FitLink href="#average-headers">{sizeReview}</FitLink>
                  {!isProductSet &&
                    !isAccessories &&
                    !shouldShowBottomSizeAndFitGuide &&
                    !shouldHideProductFitReviewPipe &&
                    '|'}
                </FitLabel>
              ) : null}
              {!isProductSet && !shouldShowBottomSizeAndFitGuide && (
                <StyledProductDetailSizeLinks
                  product={product}
                  handleShopMySize={handleShopMySize}
                  isAllGenderProduct={isAllGenderProduct}
                />
              )}
            </ProductFitReview>
          ) : (
            !isProductSet &&
            !shouldHideFitAndSizeGuide &&
            !shouldShowBottomSizeAndFitGuide && (
              <StyledProductDetailSizeLinks
                handleShopMySize={handleShopMySize}
                product={product}
                isAllGenderProduct={isAllGenderProduct}
              />
            )
          )}
        </SizeCategoryLabel>
        {renderLowSizeStockFlag()}
        <SizeSectionWrap>{sizeList}</SizeSectionWrap>
        {!isUnavailableSize && isAllGenderProduct && selectedSize.value ? (
          <AllGenderFitLabel>
            <StyledBoldAllGenderFitLabel>
              <FormattedMessage
                defaultMessage="Fits like:"
                id="site_pdp.all_gender_fit_label"
              />
            </StyledBoldAllGenderFitLabel>
            {renderAllGenderFitEquivalent()}
          </AllGenderFitLabel>
        ) : null}
      </SizeCategoryWrapper>
    );
  };

  const getSizeReview = () => {
    if (reviews && reviews.fits && reviews.fits.length > 0) {
      const maxRating = Math.max.apply(
        Math,
        reviews.fits.map(function (o) {
          return o.rating;
        })
      );

      let size =
        maxRating > 0
          ? reviews.fits.filter(field => field.rating === maxRating)
          : null;
      if (size && size.length > 1) {
        size = [{ label: 'True to size' }];
      }
      if (size && size[0]) {
        return size[0].label;
      } else {
        return '';
      }
    } else {
      return '';
    }
  };

  const renderShoeSizes = () => {
    const sizesAvailableForColors = getSizesAvailableForColors(product);

    const sizeList = shoeSizeList.map(({ label, value: actualSize }) => {
      const displayedSize = label;
      if (actualSize) {
        const sizeInfo = sizes.find(size => size.value === actualSize);

        const isSizeAvailableForColor = sizesAvailableForColors.some(
          sizeAvailable => {
            return sizeAvailable.value === actualSize;
          }
        );
        const isAvailable =
          shouldForceAvailable ||
          (sizeInfo && !sizeInfo.isSoldOut && isSizeAvailableForColor);
        const className = isAvailable ? 'available' : 'unavailable';

        if (!sizeInfo) {
          return null;
        }

        return (
          <SizeCheckbox
            className={className}
            checked={selectedSize && actualSize === selectedSize.value}
            key={actualSize}
            value={actualSize}
            label={displayedSize}
            name={actualSize}
            onChange={() => {
              onChooseSize(sizeInfo || actualSize, 'size');
            }}
            isAvailable={isAvailable}
          />
        );
      }
    });

    const sizeReview = getSizeReview();

    return (
      <SizeCategoryWrapper
        shouldShowBottomSizeAndFitGuide={shouldShowBottomSizeAndFitGuide}
        isProductSet={isProductSet}
        isProductUnisex={isProductUnisex}
      >
        <SizeCategoryLabel>
          {error ? (
            renderError(error)
          ) : (
            <FormattedMessage defaultMessage="Size" id="site_pdp.size_label" />
          )}

          {!error && !shouldHideFitAndSizeGuide && sizeReview ? (
            <ProductFitReview>
              <FitLabel defaultMessage="Fit" id="site_pdp.fit_label">
                <FormattedMessage
                  defaultMessage="Fit"
                  id="site_pdp.fit_label"
                />
                <span>:</span>
                <FitLink href="#average-headers">{sizeReview}</FitLink>
                {!isProductSet &&
                  !shouldShowBottomSizeAndFitGuide &&
                  !shouldHideProductFitReviewPipe &&
                  '|'}
              </FitLabel>
              {!isProductSet && !shouldShowBottomSizeAndFitGuide && (
                <StyledProductDetailSizeLinks
                  product={product}
                  handleShopMySize={handleShopMySize}
                />
              )}
            </ProductFitReview>
          ) : (
            !isProductSet &&
            !shouldHideFitAndSizeGuide &&
            !shouldShowBottomSizeAndFitGuide && (
              <StyledProductDetailSizeLinks
                product={product}
                handleShopMySize={handleShopMySize}
              />
            )
          )}
        </SizeCategoryLabel>
        {renderLowSizeStockFlag()}
        <SizeSectionWrap>{sizeList}</SizeSectionWrap>
      </SizeCategoryWrapper>
    );
  };

  const renderSlipperOrHarnessSizes = () => {
    const sizesAvailableForColors = getSizesAvailableForColors(product);
    const slipperHarnessSizes = isSlippers ? slipperSizeList : harnessSizeList;

    const sizeList = slipperHarnessSizes.map(({ label, value: actualSize }) => {
      const displayedSize = label;
      if (actualSize) {
        const sizeInfo = sizes.find(size => size.value === actualSize);

        const isSizeAvailableForColor = sizesAvailableForColors.some(
          sizeAvailable => {
            return sizeAvailable.value === actualSize;
          }
        );
        const isAvailable =
          shouldForceAvailable ||
          (sizeInfo && !sizeInfo.isSoldOut && isSizeAvailableForColor);
        const className = isAvailable ? 'available' : 'unavailable';

        if (!sizeInfo) {
          return null;
        }

        return (
          <SizeCheckbox
            className={className}
            checked={selectedSize && actualSize === selectedSize.value}
            key={actualSize}
            value={actualSize}
            label={displayedSize}
            name={actualSize}
            onChange={() => {
              onChooseSize(sizeInfo || actualSize, 'size');
            }}
            isAvailable={isAvailable}
          />
        );
      }
    });

    const sizeReview = getSizeReview();

    return (
      <SizeCategoryWrapper
        shouldShowBottomSizeAndFitGuide={shouldShowBottomSizeAndFitGuide}
        isProductSet={isProductSet}
        isProductUnisex={isProductUnisex}
      >
        <SizeCategoryLabel>
          {error ? (
            renderError(error)
          ) : (
            <FormattedMessage defaultMessage="Size" id="site_pdp.size_label" />
          )}

          {!error && !shouldHideFitAndSizeGuide && sizeReview ? (
            <ProductFitReview>
              <FitLabel defaultMessage="Fit" id="site_pdp.fit_label">
                <FormattedMessage
                  defaultMessage="Fit"
                  id="site_pdp.fit_label"
                />
                <span>:</span>
                <FitLink href="#average-headers">{sizeReview}</FitLink>
                {!isProductSet &&
                  !shouldShowBottomSizeAndFitGuide &&
                  !shouldHideProductFitReviewPipe &&
                  '|'}
              </FitLabel>
              {!isProductSet && !shouldShowBottomSizeAndFitGuide && (
                <StyledProductDetailSizeLinks
                  product={product}
                  handleShopMySize={handleShopMySize}
                />
              )}
            </ProductFitReview>
          ) : (
            !isProductSet &&
            !shouldHideFitAndSizeGuide &&
            !shouldShowBottomSizeAndFitGuide && (
              <StyledProductDetailSizeLinks
                product={product}
                handleShopMySize={handleShopMySize}
              />
            )
          )}
        </SizeCategoryLabel>
        {renderLowSizeStockFlag()}
        <SizeSectionWrap>{sizeList}</SizeSectionWrap>
      </SizeCategoryWrapper>
    );
  };

  const renderBraSize = () => {
    const bandSizesSelected = selectedSize ? selectedSize.band : '';
    const cupSizeSelected = selectedSize ? selectedSize.cup : '';
    const sizesAvailableForColors = getSizesAvailableForColors(product);
    const bandRadioGroup = bandSizesList
      ? bandSizesList.map(size => {
          const sizeInfos = categorySizes.filter(
            categorySize => categorySize.band === size
          );
          if (!sizeInfos.length) {
            return null;
          }
          const isSizeAvailableForColor = sizesAvailableForColors.some(
            sizeAvailable => {
              return sizeAvailable.band === size;
            }
          );

          const isNotSoldOut = sizeInfos.some(info => !info.isSoldOut);
          const isAvailable =
            shouldForceAvailable ||
            (sizeInfos && isNotSoldOut && isSizeAvailableForColor);

          const label = (
            <BandSizeWrapper
              data-autotag={isProductSet ? 'pdpSetBandSizes' : 'pdpBandSizes'}
            >
              <BandListLabel>{size}</BandListLabel>
            </BandSizeWrapper>
          );

          return (
            <div
              data-autotag={isProductSet ? 'pdpIsSetBraSize' : 'pdpIsBraSize'}
              key={size}
            >
              <BraCheckbox
                checked={size === bandSizesSelected}
                value={size}
                label={label}
                name={size}
                onChange={() => {
                  onChooseSize(size, 'bandSize');
                }}
                isAvailable={isAvailable}
              />
            </div>
          );
        })
      : [];

    const cupRadioGroup = cupSizesList
      ? cupSizesList.map(cupSize => {
          if (cupSize != null) {
            const sizeInfo = categorySizes.find(
              categorySize => categorySize.cup === cupSize
            );
            if (!sizeInfo) {
              return null;
            }

            const label = (
              <CupSizeWrapper
                data-autotag={isProductSet ? 'pdpSetCupSizes' : 'pdpCupSizes'}
              >
                <CupLabel>{cupSize}</CupLabel>
              </CupSizeWrapper>
            );

            const isSizeAvailableForColor =
              shouldForceAvailable ||
              sizesAvailableForColors.some(
                sizeAvailable => sizeAvailable.cup === cupSize
              );

            if (!isSizeAvailableForColor) {
              return null;
            }
            /** isAvailable is existent combination or soldout **/
            const isAvailable = categorySizes.some(
              brasize =>
                brasize.cup === cupSize &&
                (brasize.band === bandSizesSelected || !bandSizesSelected) &&
                !brasize.isSoldOut
            );
            let name = cupSize;
            let size = cupSize;
            let field = 'cupSize';
            if (bandSizesSelected) {
              const categorySize = categorySizes.find(
                ({ band, cup }) => band === bandSizesSelected && cup === cupSize
              );
              field = 'braSize';
              /** both band and cup are selected and exist **/
              if (categorySize) {
                size = categorySize;
                name = categorySize.value;
              } else {
                /** if combination doesn't exist, we create and pass the size, to have it selected and displayed in the error message **/
                size = bandSizesSelected.concat(cupSize);
              }
            }

            return (
              <CupSizeCheckbox
                checked={cupSize === cupSizeSelected}
                key={name}
                value={name}
                label={label}
                name={name}
                onChange={() => onSelectCupSize(size, cupSize, field)}
                isAvailable={isAvailable}
              />
            );
          }
        })
      : [];

    const sizeReview = getSizeReview();

    return (
      <SizeCategoryWrapper
        shouldShowBottomSizeAndFitGuide={shouldShowBottomSizeAndFitGuide}
        isProductSet={isProductSet}
      >
        <BraSizeCategoryLabel>
          {error && error === SIZE_REQUIRED ? (
            renderError(error, 'band')
          ) : (
            <FormattedMessage
              defaultMessage="Band"
              id="site_pdp.band_size_label"
            />
          )}

          {(!error || error !== SIZE_REQUIRED) &&
          !shouldHideFitAndSizeGuide &&
          sizeReview ? (
            <ProductFitReview>
              <FitLabel>
                <FormattedMessage
                  defaultMessage="Fit"
                  id="site_pdp.fit_label"
                />
                :<FitLink href="#average-headers">{sizeReview}</FitLink>
                {!isProductSet &&
                  !shouldShowBottomSizeAndFitGuide &&
                  !shouldHideProductFitReviewPipe &&
                  '|'}
              </FitLabel>
              {!isProductSet && !shouldShowBottomSizeAndFitGuide && (
                <StyledProductDetailSizeLinks
                  product={product}
                  handleShopMySize={handleShopMySize}
                />
              )}
            </ProductFitReview>
          ) : (
            !isProductSet &&
            !shouldHideFitAndSizeGuide &&
            !shouldShowBottomSizeAndFitGuide && (
              <StyledProductDetailSizeLinks
                product={product}
                handleShopMySize={handleShopMySize}
              />
            )
          )}
        </BraSizeCategoryLabel>
        <SizeSectionWrap>{bandRadioGroup}</SizeSectionWrap>
        {renderLowSizeStockFlag()}
        <BraSizeCategoryLabel>
          {error ? (
            renderError(error, 'cup')
          ) : (
            <FormattedMessage
              defaultMessage="Cup"
              id="site_pdp.cup_size_label"
            />
          )}
        </BraSizeCategoryLabel>
        <SizeSectionWrap>{cupRadioGroup}</SizeSectionWrap>
      </SizeCategoryWrapper>
    );
  };

  const renderSizeSelector = () => {
    if (Array.isArray(sizes) && !sizes.length) {
      return null;
    }
    const isOneSize = sizes[0].isOneSize;

    if (hasShoeSize(product)) {
      return renderShoeSizes();
    }

    if (isSlippers || isHarness) {
      return renderSlipperOrHarnessSizes();
    }

    if (displayBraSize) {
      return renderBraSize();
    }

    if (isOneSize) {
      return renderOneSize();
    }

    return renderSizes();
  };

  return (
    <Container
      shouldShowBottomSizeAndFitGuide={shouldShowBottomSizeAndFitGuide}
    >
      {renderSizeSelector()}
      {!isProductSet &&
        !shouldHideFitAndSizeGuide &&
        shouldShowBottomSizeAndFitGuide && (
          <StyledProductDetailSizeLinks
            product={product}
            shouldShowBottomSizeAndFitGuide
            handleShopMySize={handleShopMySize}
          />
        )}
    </Container>
  );
}

ProductDetailSizeSelector.propTypes = {
  error: PropTypes.string,
  displayBraSize: PropTypes.bool,
  getSizeLabel: PropTypes.func.isRequired,
  isCombinedSet: PropTypes.bool,
  isGwpSelection: PropTypes.bool,
  /**
   * is Set
   */
  isProductSet: PropTypes.bool,
  isProductUnisex: PropTypes.bool,
  label: PropTypes.object,
  /**
   * callback with size information selected
   */
  onChooseSize: PropTypes.func,
  product: PropTypes.object,
  selectedColorLabel: PropTypes.string,
  /**
   * currently selected size
   */
  selectedSize: PropTypes.object,
  setIndex: PropTypes.number,
  /**
   * sizes list
   */
  sizes: PropTypes.array,
  shouldForceAvailable: PropTypes.bool,
  shouldHideFitAndSizeGuide: PropTypes.bool,
  shouldHideProductFitReviewPipe: PropTypes.bool,
  shouldShowBottomSizeAndFitGuide: PropTypes.bool,
  shouldSplitCombinedSize: PropTypes.bool,
};

ProductDetailSizeSelector.BraSizeCategoryLabel = BraSizeCategoryLabel;
ProductDetailSizeSelector.SizeCategoryLabel = SizeCategoryLabel;
ProductDetailSizeSelector.SizeCategoryWrapper = SizeCategoryWrapper;
ProductDetailSizeSelector.SizeSectionWrap = SizeSectionWrap;
ProductDetailSizeSelector.SizeGuideButton =
  ProductDetailSizeLinks.SizeGuideButton;
ProductDetailSizeSelector.ProductFitReview = ProductFitReview;
ProductDetailSizeSelector.FitLabel = FitLabel;
ProductDetailSizeSelector.FitLink = FitLink;

export default ProductDetailSizeSelector;
